import { useLocation, useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import Header from 'components/headers/addLoan'
import DotsLoading from 'components/loadings/dotsLoading'
import { useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { toast } from 'react-toastify'
import useSetInitPayment from './hooks/useSetInitPayment'
import useSetPay from './hooks/useSetPay'
import useRecordInstallments from './hooks/useRecordInstallments'
import RecordPaymentModal from './RecordPaymentModal'
import GatewayPicker from 'components/gatewayPicker'
import ManualPaymentCard from 'components/manualPayment'
import useAvailableGateways from 'utilities/hooks/useAvailableGateways'
import useBoxManualPayment from 'modules/easyPay/hooks/useBoxManualPayment'
import { faPlain, toman } from 'utilities/helper/format'
import CommissionHint from 'components/commissionHint'
import {
    buildPaymentItems,
    commissionOf,
    installmentsFromState,
    poolshipFromState,
    totalOf,
} from './payload'

/**
 * The repayment receipt — one or many installments, one trip to the bank.
 *
 * The loan page sends a basket here (`state.installments`); everything below is
 * sized off that list rather than off a single installment, so «تسویه کامل» of
 * a twelve-month loan is this same screen with twelve lines behind the total.
 *
 * The torn-paper receipt this replaces was a fixed-height absolutely-positioned
 * card with its own drop shadow — it predates the home page, and it could not
 * grow: a twelve-installment breakdown ran under the pay button. Now it is the
 * same white-on-canvas card the rest of the app uses, in normal flow.
 */
export default function PayForOtherInLoan() {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const { state } = useLocation()
    const { moneyPool } = useParams()

    const installments = useMemo(() => installmentsFromState(state), [state])
    const poolshipId = useMemo(() => poolshipFromState(state), [state])

    // Which door the screen was opened by. `installments` is the basket the
    // loan page builds; anything else arrived through the older
    // single-installment shape, which is the path the «پرداخت به دیگری» modal
    // still takes — and the reason «selected» reads far below «initiated».
    // Deliberately the same test `installmentsFromState` makes, so the label
    // always names the shape the screen is actually reading.
    const entry: 'multi_select' | 'legacy_single' =
        Array.isArray(state?.installments) && state.installments.length > 0
            ? 'multi_select'
            : 'legacy_single'

    const { data, isLoading } = useSetInitPayment(poolshipId, installments)
    const { mutate, isLoading: isPaying } = useSetPay()
    const { record, isRecording } = useRecordInstallments(
        moneyPool,
        state?.loanId,
        state?.actorRole
    )
    // Ask which gateways will actually take a payment for THIS fund, rather
    // than reading `init_pay`'s list. That list is already fund-filtered here,
    // which is exactly why this screen used to show no gateway at all on the
    // ~29k funds Pasargad is not live on: the filtered answer is
    // کارت‌به‌کارت alone, and the old picker dropped it.
    const { gateways, selectedCode, setSelectedCode, hasNoGateway } =
        useAvailableGateways(
            useMemo(
                () =>
                    moneyPool
                        ? [{ type: 'mnp' as const, id: Number(moneyPool) }]
                        : [],
                [moneyPool]
            ),
            { fallback: data, enabled: !!moneyPool }
        )

    const { info: manualPayment, isSettled: isManualSettled } =
        useBoxManualPayment(Number(moneyPool), hasNoGateway)

    // A repayment that reached the receipt and has nowhere to go. Counted
    // because it is a dead end the funnel cannot see: the payer neither pays
    // nor fails, they just stop. Held until the manual-details lookup has
    // answered, otherwise every fund would be reported as having no way out.
    const noGatewayLogged = useRef(false)
    useEffect(() => {
        if (!hasNoGateway || !isManualSettled || noGatewayLogged.current) return
        noGatewayLogged.current = true
        posthog.capture('payment_no_gateway_available', {
            flow_type: 'loan_installment',
            has_manual_fallback: !!(
                manualPayment?.phone || manualPayment?.card
            ),
        })
    }, [hasNoGateway, isManualSettled, manualPayment])

    const amount = totalOf(installments)
    // Read the fee off the per-receiver quote rather than `data.commission`:
    // the payment items are built from the same array, so the number shown and
    // the number charged cannot drift apart.
    const commission = commissionOf(data?.receivers)
    const count = installments.length

    // Once the quote is in, not on mount: before it lands the fee is unknown
    // and `hasNoGateway` has not settled, so an early capture would report a
    // screen nobody has actually seen yet.
    const hasReportedView = useRef(false)
    useEffect(() => {
        if (!data || count === 0 || hasReportedView.current) return
        hasReportedView.current = true
        posthog.capture('loan_installment_payment_viewed', {
            moneypool_id: moneyPool,
            installment_count: count,
            amount,
            commission,
            has_no_gateway: hasNoGateway,
            is_manager: !!state?.isManager,
            entry,
        })
    }, [
        data,
        count,
        moneyPool,
        amount,
        commission,
        hasNoGateway,
        state?.isManager,
        entry,
    ])

    // The dead end, counted. The screen shows کارت‌به‌کارت instructions and
    // there is nothing more to press, so nothing downstream would ever record
    // that this member could not pay through a gateway at all.
    const hasReportedNoGateway = useRef(false)
    useEffect(() => {
        if (!data || count === 0 || !hasNoGateway) return
        if (hasReportedNoGateway.current) return
        hasReportedNoGateway.current = true
        posthog.capture('loan_installment_payment_blocked_no_gateway', {
            moneypool_id: moneyPool,
            installment_count: count,
            amount,
        })
    }, [data, count, hasNoGateway, moneyPool, amount])

    const onPay = () => {
        if (!selectedCode) {
            toast.error('روش پرداخت را انتخاب کنید')
            return
        }
        const paymentItems = buildPaymentItems(
            installments,
            data?.receivers,
            Number(moneyPool)
        )
        if (!paymentItems) {
            // The quote did not line up with the basket; paying now would
            // attach the wrong fee to an installment.
            posthog.capture('loan_installment_quote_mismatch', {
                moneypool_id: moneyPool,
                selected_count: count,
                quoted_count: data?.receivers?.length ?? 0,
            })
            toast.error('اطلاعات پرداخت به‌روز نیست. دوباره تلاش کنید.')
            return
        }
        posthog.capture('loan_installment_payment_initiated', {
            amount,
            commission,
            installment_count: count,
            moneypool_id: moneyPool,
            gateway: selectedCode,
            entry,
        })
        mutate({
            paymentItems,
            gateway: { uniqueCode: selectedCode },
            clientType: 'pwa',
        })
    }

    if (!data || count === 0 || isLoading) {
        return (
            <div className="min-h-screen bg-canvas">
                <Header title="پرداخت قسط" />
                <DotsLoading />
            </div>
        )
    }

    return (
        <>
            <div className="min-h-screen bg-canvas pb-8">
                <Header title={count > 1 ? 'پرداخت اقساط' : 'پرداخت قسط'} />

                <div className="px-4 pt-2">
                    <div className="bg-white border border-divider rounded-card shadow-card p-4">
                        <p className="text-xs text-text-secondary text-center">
                            قابل پرداخت
                        </p>
                        <p className="mt-[2px] text-center text-[26px] font-bold text-heading leading-tight">
                            {toman(amount + commission)}
                        </p>

                        <div className="mt-4 flex flex-col gap-2 border-t border-divider pt-3">
                            <Row
                                label={
                                    count > 1
                                        ? `مبلغ ${faPlain.format(count)} قسط`
                                        : 'مبلغ قسط'
                                }
                                value={toman(amount)}
                            />
                            <Row
                                label={<CommissionHint />}
                                value={toman(commission)}
                            />
                            {state?.name && (
                                <Row label="صندوق" value={state.name} />
                            )}
                        </div>

                        {count > 1 && (
                            <div className="mt-3 border-t border-divider pt-3">
                                <p className="mb-2 text-[13px] text-text-secondary">
                                    اقساطی که پرداخت می‌شوند
                                </p>
                                <div className="flex flex-col gap-1.5">
                                    {installments.map((installment) => (
                                        <div
                                            key={installment.id}
                                            className="flex items-baseline justify-between gap-3"
                                        >
                                            <span className="text-sm text-text-body shrink-0">
                                                {installment.index
                                                    ? `قسط ${faPlain.format(
                                                          installment.index
                                                      )}`
                                                    : 'قسط'}
                                            </span>
                                            <span className="text-sm font-medium text-text-body truncate">
                                                {toman(installment.amount)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-5">
                        {hasNoGateway ? (
                            // No gateway can take this fund's money. The screen
                            // used to render an empty logo row and a pay button
                            // that could only ever toast «درگاه پرداخت را
                            // انتخاب کنید» — a dead end with no way out.
                            <ManualPaymentCard
                                info={manualPayment}
                                boxName={state?.name}
                                amount={amount}
                                source="loan_installment"
                                viewerIsManager={!!state?.isManager}
                            />
                        ) : (
                            <>
                                <GatewayPicker
                                    gateways={gateways}
                                    selectedCode={selectedCode}
                                    onSelect={setSelectedCode}
                                />
                                <button
                                    type="button"
                                    onClick={onPay}
                                    disabled={isPaying}
                                    className="mt-6 w-full rounded-full bg-green-1 py-2.5 text-[15px] font-bold text-white active:scale-[0.99] transition-transform motion-reduce:transition-none disabled:opacity-60"
                                >
                                    {isPaying
                                        ? 'در حال انتقال به درگاه…'
                                        : count > 1
                                        ? `پرداخت ${faPlain.format(count)} قسط`
                                        : 'پرداخت قسط'}
                                </button>
                            </>
                        )}
                    </div>

                    {state?.isManager && (
                        <>
                            {/* Recording needs no gateway — the money is
                                already in the manager's hands. The old button
                                demanded one before it would even open the
                                confirm. */}
                            <button
                                type="button"
                                onClick={() => {
                                    // opening the confirm, not recording yet —
                                    // the pair with `loan_installments_recorded`
                                    // is what shows how many managers back out
                                    // of the modal
                                    posthog.capture(
                                        'loan_installments_record_started',
                                        {
                                            moneypool_id: moneyPool,
                                            loan_id: state?.loanId,
                                            count,
                                            amount,
                                            actor_role: state?.actorRole,
                                        }
                                    )
                                    setIsOpen(true)
                                }}
                                // Filled when there is no gateway block above
                                // it: recording is then the ONLY thing this
                                // screen can do, and a lone ghost button reads
                                // as the secondary option to something that is
                                // not on the page.
                                className={`mt-2 w-full rounded-full py-2.5 text-[15px] font-bold active:scale-[0.99] transition-transform motion-reduce:transition-none ${
                                    hasNoGateway
                                        ? 'bg-green-1 text-white'
                                        : 'border border-green-1 bg-white text-green-1'
                                }`}
                            >
                                {count > 1
                                    ? `ثبت پرداخت ${faPlain.format(count)} قسط`
                                    : 'ثبت پرداخت'}
                            </button>
                            {/* Just the instruction. Advertising «کارمزدی
                                ندارد» here reads as a recommendation to take
                                the money by hand instead of through the
                                gateway — the modal explains what recording
                                means, and that is the right place for it.

                                Dropped when there is no gateway: the block
                                above the button already says «مبلغی را که
                                دستی دریافت کرده‌اید، همین‌جا ثبت کنید», and
                                the same sentence twice around one button
                                reads as two different instructions. */}
                            {!hasNoGateway && (
                                <p className="mt-2 text-center text-xs text-text-secondary">
                                    اگر مبلغ را دستی گرفته‌اید «ثبت پرداخت» را
                                    بزنید.
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
            <RecordPaymentModal
                show={isOpen}
                onHide={() => setIsOpen(false)}
                count={count}
                amount={amount}
                isRecording={isRecording}
                onConfirm={(description) => {
                    setIsOpen(false)
                    record(count, description)
                }}
            />
        </>
    )
}

/**
 * Label on the right, value on the left — the page is already RTL.
 *
 * The label is a node, not a string, so a row can carry a disclosure under it
 * (see `CommissionHint`). That is why the label cell is a growable block
 * rather than a `shrink-0` inline span: a note wrapping to two lines has to
 * push the row taller, not shove the amount off the card.
 */
function Row({ label, value }: { label: ReactNode; value: string }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-sm text-text-secondary min-w-0">{label}</div>
            <span className="text-[15px] font-medium text-text-body truncate shrink-0">
                {value}
            </span>
        </div>
    )
}
